import { Box, Checkbox, Divider, Typography } from '@mui/material'
import { SimpleTreeView } from '@mui/x-tree-view/SimpleTreeView'
import { TreeItem } from '@mui/x-tree-view/TreeItem'
import {
    CentroCusto,
    Supervisor,
    supervisor as supervisor_api,
} from 'service/api'
import { toast } from 'sonner'
import {
    INodeCentroCusto,
    mapearArvore,
} from 'templates/Cadastro/CentroCusto/mapArvore'

interface IProps {
    fetchedData?: CentroCusto[]
    supervisor: Supervisor
}

export default function ArvoreCentroCusto({ fetchedData, supervisor }: IProps) {
    const data = mapearArvore(fetchedData)
    const centrosAssociados = supervisor.SupervisorCentroCusto ?? []

    function montarArvore(node: CentroCusto & INodeCentroCusto) {
        return (
            <TreeItem
                key={node.id}
                itemId={node.id.toString()}
                sx={{ mx: 1 }}
                label={
                    <Box
                        sx={{
                            display: 'grid',
                            alignItems: 'center',
                            gridTemplateColumns: 'auto 1fr',
                            gap: 1,
                        }}
                    >
                        <SwitchAssociado
                            data={data}
                            associados={centrosAssociados}
                            node={node}
                            supervisorId={supervisor.id}
                        />
                        <Typography>
                            {node.codigo} – {node.nomeCentroCusto}
                        </Typography>
                    </Box>
                }
            >
                {Array.isArray(node.children)
                    ? node.children.map(montarArvore)
                    : null}
            </TreeItem>
        )
    }

    return (
        <Box
            sx={{
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                maxHeight: '100%',
            }}
        >
            <Box
                sx={theme =>
                    ({
                        border: 1,
                        borderColor: theme.palette.divider,
                        borderRadius: 1,
                        height: '100%',
                    }) as any
                }
            >
                <Box
                    sx={{
                        width: '100%',
                        height: '2.5rem',
                        display: 'grid',
                        gridTemplateColumns: 'min-content min-content 1fr',
                        gap: 1,
                        p: 1.5,
                        fontWeight: 500,
                        fontSize: '0.875rem',
                    }}
                >
                    <div>&nbsp;Associar&nbsp;</div>
                    <Divider
                        orientation="vertical"
                        sx={{
                            borderWidth: 1.5,
                        }}
                    />
                    <div>Centro de custo</div>
                </Box>
                <Box
                    sx={theme => ({
                        borderTop: 1,
                        borderColor: theme.palette.divider,
                        overflowY: 'auto',
                        py: 1,
                        marginTop: 0,
                        height: 'calc(100% - 2.5rem)',
                    })}
                >
                    <SimpleTreeView
                        defaultExpandedItems={fetchedData?.map(it =>
                            it.id.toString(),
                        )}
                    >
                        {data?.length ? (
                            data.map(montarArvore)
                        ) : (
                            <Typography
                                variant="body1"
                                sx={{
                                    userSelect: 'none',
                                    textAlign: 'center',
                                    mt: 2,
                                    opacity: 0.5,
                                }}
                            >
                                Nenhum resultado
                            </Typography>
                        )}
                    </SimpleTreeView>
                </Box>
            </Box>
        </Box>
    )
}

interface IPropsSwitch {
    data: INodeCentroCusto[]
    associados: Supervisor['SupervisorCentroCusto']
    node: INodeCentroCusto
    supervisorId: number
}

function SwitchAssociado({
    data = [],
    associados = [],
    supervisorId,
    node,
}: IPropsSwitch) {
    const associadosIds = new Set(associados.map(it => it.centroCustoId))
    const options = {
        invalidateQuery: true,
        onSuccess: () => supervisor_api.invalidateQueries(),
        onError: (e: any) => {
            toast.error(e.message)
        },
    }
    const { mutateAsync: associar, isLoading } =
        supervisor_api.centroCusto.mutations.useCreate(options)
    const { mutateAsync: desassociar, isLoading: isDesassociando } =
        supervisor_api.centroCusto.mutations.useDelete(options)

    function isIndeterminate(node: INodeCentroCusto): boolean {
        if (
            associados.find(it => it.centroCustoId === node.id) ||
            !node.children?.length
        )
            return false

        const noneChecked = node.children.every(
            child => !associadosIds.has(child.id),
        )

        return (
            !noneChecked || node.children.some(child => isIndeterminate(child))
        )
    }

    function collectChildrenIds(
        node: INodeCentroCusto,
        childrenOf: number,
    ): number[] {
        if (node.id === childrenOf)
            return node.children?.map(child => child.id) ?? []
        if (node.children?.length) {
            return node.children.flatMap(child =>
                collectChildrenIds(child, childrenOf),
            )
        }
        return []
    }

    function updateWithChildren(node: INodeCentroCusto) {
        if (associadosIds.has(node.id))
            desassociar(
                associados.find(it => it.centroCustoId === node.id)?.id!,
            )
        else
            associar({
                supervisorId,
                // @ts-ignore tipagem está correta
                centroCustoIds: [
                    node.id,
                    ...data.flatMap(child =>
                        collectChildrenIds(child, node.id),
                    ),
                ].filter(id => !associadosIds.has(id)),
            })
    }

    return (
        <Checkbox
            checked={!!associados.find(it => it.centroCustoId === node.id)}
            indeterminate={isIndeterminate(node)}
            disabled={isLoading || isDesassociando}
            size="small"
            onChange={() => updateWithChildren(node)}
            onClick={e => {
                e.stopPropagation()
            }}
        />
    )
}
